import React from 'react'
import PropTypes from 'prop-types'
import { trim, snakeCase } from 'lodash'
import { graph } from '@adc/constants'
import {
  InputFieldReduxFormWrapper,
  RadioGroupFieldReduxFormWrapper,
  CheckboxFieldReduxFormWrapper,
  MaskedInputFieldReduxFormWrapper,
  Grid,
  Text,
  constants as tkConstants,
  utils as tkUtils,
} from '@adc/ui-core'
import { ContactInfoFields, AddressFields } from './shared/'
import { Field } from 'redux-form'
import classnames from 'classnames/bind'

import styles from './styles.scss'
import { capitalizeFirstLetter } from './utils'

const cx = classnames.bind(styles)

const { required, format, length, exclusion } = tkUtils.formValidators

const TRUSTEE_PURCHASE_PROFILE_FORM_NAME = 'trustee_purchase_profile'

const PURCHASE_PROFILES_UNIQUE_NAME_ERROR =
  'You already have another purchase profile with this name. Please enter a unique name for this profile.'

const BASE_ELM_ID = 'trustee_purchase_profile_form'
const formKey = 'trusteePurchaseProfileInput'

const PURCHASING_FOR_SELF_OPTIONS = [
  {
    label: 'Yes',
    value: graph.enums.TrusteeCustomerType.OWNER,
    'data-elm-id': 'purchasing_for_self',
  },
  {
    label: 'No',
    value: graph.enums.TrusteeCustomerType.REPRESENTATIVE,
    'data-elm-id': 'not_purchasing_self',
  },
]

const FormSectionWrapper = ({ sectionTitle, children }) => {
  return (
    <Grid.Row>
      <Grid.Column width={4}>
        <Text.Header
          color='gray'
          level={3}
          content={sectionTitle}
          data-elm-id={`${BASE_ELM_ID}_section_title_${snakeCase(
            sectionTitle
          )}`}
        />
      </Grid.Column>
      <Grid.Column width={12}>{children}</Grid.Column>
    </Grid.Row>
  )
}

FormSectionWrapper.propTypes = {
  sectionTitle: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
}

const TrusteePurchaseProfileFormFields = ({
  purchaseProfiles = [],
  fieldValues,
  onIsPurchasingSelfChange,
}) => {
  const isRefundAddressSameAsDeedMailingAddress =
    fieldValues?.__computed__?.is_refund_address_same_as_deed_mailing_address

  const isPurchasingSelf =
    fieldValues.customer_type === graph.enums.TrusteeCustomerType.OWNER

  return (
    <div className={cx('ui', 'form')}>
      <Grid stackable>
        <FormSectionWrapper sectionTitle='Profile Information'>
          <Field
            name={`${formKey}.profile_name`}
            component={InputFieldReduxFormWrapper}
            label='Profile Name'
            elmId={`${BASE_ELM_ID}_profile_name`}
            normalizeOnBlur={trim}
            normalize={capitalizeFirstLetter}
            type='text'
            required
            validate={[
              required(),
              length({ max: 100 }),
              format({
                without: /[^A-Za-z0-9 \-!@#$%^&*()\\_[\]=+/{}';:<>,.?]/i,
                msg: 'Characters ` ~ | " are not allowed',
              }),
              exclusion({
                in: purchaseProfiles
                  .filter(
                    profile =>
                      profile.purchase_profile_id !==
                      fieldValues.purchase_profile_id
                  )
                  .map(profile => profile.profile_name),
                msg: PURCHASE_PROFILES_UNIQUE_NAME_ERROR,
              }),
            ]}
          />
          <Field
            name={`${formKey}.customer_type`}
            component={RadioGroupFieldReduxFormWrapper}
            onChange={onIsPurchasingSelfChange}
            options={PURCHASING_FOR_SELF_OPTIONS}
            elmId={`${BASE_ELM_ID}_customer_type`}
            label='Are you purchasing for yourself ?'
          />
        </FormSectionWrapper>
        <FormSectionWrapper sectionTitle='Owner Information'>
          <ContactInfoFields
            elmId={`${BASE_ELM_ID}_contact_info`}
            formKey={`${formKey}.contact_info`}
            contactFieldValues={fieldValues.contact_info}
          />
          <AddressFields
            elmId={`${BASE_ELM_ID}_contact_info_contact_address`}
            formKey={`${formKey}.contact_info.contact_address`}
          />
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKey}.contact_info.phone`}
              component={MaskedInputFieldReduxFormWrapper}
              label='Phone Number'
              elmId={`${BASE_ELM_ID}_contact_info_phone`}
              validate={required()}
              mask={tkUtils.inputMasks.phoneNumberMask}
              normalize={value => value.replace(/[ ()-]/g, '')}
              type='text'
              required
            />
          </div>
          <div className={cx('field-wrapper')}>
            <Field
              disabled={isPurchasingSelf}
              name={`${formKey}.contact_info.email`}
              component={InputFieldReduxFormWrapper}
              label='E-mail Address'
              elmId={`${BASE_ELM_ID}_email`}
              validate={[
                required(),
                format({
                  with: tkConstants.regex.EMAIL_SYNTAX,
                  msg: 'Invalid email',
                }),
              ]}
              normalizeOnBlur={trim}
              type='text'
              required
            />
          </div>
        </FormSectionWrapper>
        <FormSectionWrapper sectionTitle='Deed Mailing Address'>
          <ContactInfoFields
            elmId={`${BASE_ELM_ID}_deed_mailing_address`}
            formKey={`${formKey}.deed_mailing_address`}
            contactFieldValues={fieldValues.deed_mailing_address}
          />
          <AddressFields
            elmId={`${BASE_ELM_ID}_deed_mailing_address`}
            formKey={`${formKey}.deed_mailing_address`}
          />
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKey}.deed_mailing_address.phone`}
              component={MaskedInputFieldReduxFormWrapper}
              label='Phone Number'
              elmId={`${BASE_ELM_ID}_deed_mailing_address_phone`}
              validate={required()}
              mask={tkUtils.inputMasks.phoneNumberMask}
              normalize={value => value.replace(/[ ()-]/g, '')}
              type='text'
              required
            />
          </div>
        </FormSectionWrapper>
        <FormSectionWrapper sectionTitle='Refund Payable To'>
          <Field
            name={`${formKey}.__computed__.is_refund_address_same_as_deed_mailing_address`}
            component={CheckboxFieldReduxFormWrapper}
            options={PURCHASING_FOR_SELF_OPTIONS}
            elmId={`${BASE_ELM_ID}_is_refund_address_same_as_deed_mailing_address`}
            checkboxLabel='Same as Deed Mailing Address'
            type='checkbox'
          />
          {!isRefundAddressSameAsDeedMailingAddress && (
            <>
              <ContactInfoFields
                elmId={`${BASE_ELM_ID}_refund_payable_to_address`}
                formKey={`${formKey}.refund_payable_to_address`}
                contactFieldValues={fieldValues.refund_payable_to_address}
              />
              <AddressFields
                elmId={`${BASE_ELM_ID}_refund_payable_to_address`}
                formKey={`${formKey}.refund_payable_to_address`}
              />
              <div className={cx('field-wrapper')}>
                <Field
                  name={`${formKey}.refund_payable_to_address.phone`}
                  component={MaskedInputFieldReduxFormWrapper}
                  label='Phone Number'
                  elmId={`${BASE_ELM_ID}_refund_payable_to_address_phone`}
                  validate={required()}
                  mask={tkUtils.inputMasks.phoneNumberMask}
                  normalize={value => value.replace(/[ ()-]/g, '')}
                  type='text'
                  required
                />
              </div>
            </>
          )}
        </FormSectionWrapper>
        <FormSectionWrapper sectionTitle='Vesting Details'>
          <div className={cx('field-wrapper')}>
            <Field
              name={`${formKey}.vesting_instructions`}
              component={InputFieldReduxFormWrapper}
              label='How should the purchase be legally vested'
              elmId={`${BASE_ELM_ID}_vesting_instructions`}
              validate={required()}
              normalizeOnBlur={trim}
              normalize={capitalizeFirstLetter}
              type='text'
              required
            />
          </div>
        </FormSectionWrapper>
      </Grid>
    </div>
  )
}

TrusteePurchaseProfileFormFields.propTypes = {
  fieldValues: PropTypes.object,
  onIsPurchasingSelfChange: PropTypes.func.isRequired,
  purchaseProfiles: PropTypes.arrayOf(PropTypes.object),
}

export default TrusteePurchaseProfileFormFields
export { TRUSTEE_PURCHASE_PROFILE_FORM_NAME }
